"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { motion } from "framer-motion"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Eye, ShoppingCart } from "lucide-react"
import ProductShowcase from "./3d/product-showcase"

interface Product {
  id: number
  name: string
  category: string | null
  subcategory: string | null
  image: string
  alt: string
  meta: {
    title: string
    description: string
    keywords: string
  }
}

interface ModernProductCardProps {
  product: Product
  index?: number
}

export default function ModernProductCard({ product, index = 0 }: ModernProductCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [show3D, setShow3D] = useState(false)

  // Add safety checks
  if (!product || !product.meta) {
    return null
  }

  const slug = product.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "")

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -10 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <Card className="group relative overflow-hidden bg-white/80 backdrop-blur-sm border-0 shadow-lg hover:shadow-2xl transition-all duration-500">
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

        <CardContent className="p-0 relative">
          <div className="relative aspect-square overflow-hidden">
            {show3D ? (
              <ProductShowcase productType={product.category ?? ""} />
            ) : (
              <div className="relative w-full h-full">
                <Image
                  src={`${product.image}`}
                  alt={product.alt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-110"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 33vw, 25vw"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            )}

            {/* Category badge */}
            {product.category && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="absolute top-3 left-3"
              >
                <Badge className="bg-white/90 text-slate-900 backdrop-blur-sm border-0 shadow-lg">
                  {product.category}
                </Badge>
              </motion.div>
            )}

            {/* 3D Toggle */}
            {/* <motion.button
              className="absolute top-3 right-3 p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300"
              onClick={() => setShow3D(!show3D)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <Eye className="h-4 w-4 text-slate-700" />
            </motion.button> */}

            {/* Hover overlay */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-4"
              initial={false}
              animate={{ opacity: isHovered ? 1 : 0 }}
            >
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: isHovered ? 0 : 20, opacity: isHovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100" asChild>
                  <Link href={`/products/${slug}-${product.id}`} aria-label={`View details for ${product.name}`}>
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    View Details
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>

          <div className="p-6">
            <motion.h3
              className="font-semibold text-lg mb-3 line-clamp-2 group-hover:text-slate-600 transition-colors"
              whileHover={{ x: 5 }}
            >
              {product.name}
            </motion.h3>

            <p className="text-slate-600 text-sm line-clamp-3 mb-4 leading-relaxed">
              {product.meta?.description || "No description available"}
            </p>

            <div className="flex items-center justify-between">
              {product.subcategory && (
                <Badge variant="outline" className="text-xs border-slate-200">
                  {product.subcategory}
                </Badge>
              )}

              {/* <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100/50"
                  asChild
                >
                  <Link href={`/products/${slug}-${product.id}`}>Learn More →</Link>
                </Button>
              </motion.div> */}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
